"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import apiCall from "@/libs/api";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

const ArticleCard = ({
  article,
  href,
}: {
  article: APIDataSpacesArticle;
  href: string;
}) => {
  const t = useTranslations("web.pages.home.sections.articles");
  const date = new Date(article.dates.created);
  const formattedDate = date.toLocaleDateString();

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-xl p-4 hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      <div className="flex items-start justify-between mb-3">
        {article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {article.tags.slice(0, 2).map((tag) => (
              <TFBadge key={tag.ref} title={tag.title} />
            ))}
          </div>
        )}
      </div>

      <h3 className="text-base font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-black/70 transition-colors">
        {article.title}
      </h3>
      <p className="text-sm text-slate-600 mb-4 line-clamp-3 flex-grow">
        {article.description}
      </p>

      <div className="flex flex-col gap-2 text-xs text-slate-500">
        <span className="font-medium">{article.space.name}</span>
        <span>{formattedDate}</span>
      </div>
    </TFLink>
  );
};

export default function SectionArticles() {
  const t = useTranslations("web.pages.home.sections.articles");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [itemsPerView, setItemsPerView] = useState(3);
  const [articles, setArticles] = useState<APIDataSpacesArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/spaces/articles", {
          action: "publicList",
          locale: locale as UILocale,
        });
        setArticles((response as any).articles);
      } catch (error) {
        console.error("Failed to load articles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadArticles();
    }
  }, [locale]);

  const latestArticles = useMemo(() => {
    return [...articles]
      .sort(
        (a, b) =>
          new Date(b.dates.created).getTime() -
          new Date(a.dates.created).getTime(),
      )
      .slice(0, 6);
  }, [articles]);

  const categoriesCount = useMemo(() => {
    const tagSet = new Set<string>();
    articles.forEach((article) => {
      article.tags.forEach((tag) => {
        tagSet.add(tag.ref);
      });
    });
    return tagSet.size;
  }, [articles]);

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allArticles"),
        href: "/articles",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [
          { title: t("totalArticles"), value: articles.length },
          { title: t("categories"), value: categoriesCount },
        ],
        position: "right",
      }}
      className="bg-black/2"
    >
      {/* Articles slider */}
      {isLoading || latestArticles.length === 0 ? null : (
        <TFHorizontalSlider
          items={latestArticles}
          renderItem={(article) => (
            <ArticleCard article={article} href={`/articles/${article.ref}`} />
          )}
          gap={16}
          autoScroll={false}
          showControls={true}
          itemsPerView={itemsPerView}
          showDots={true}
        />
      )}
    </TFLayoutHomeSection>
  );
}
